export const mobilityLinePricingModes = [
    { value: 'flatPerLine', label: 'Flat price per line' },
    { value: 'firstLineAdditional', label: 'First line + additional lines' },
    { value: 'totalLineCountTier', label: 'Tier by number of lines' }
];

const validModes = new Set(mobilityLinePricingModes.map((mode) => mode.value));
const normalizeArray = (value) => (Array.isArray(value) ? value : []);

const numberFromInput = (value, fallback = 0) => {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : fallback;
};

const formatMoney = (value) => {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) return String(value);
    return Number.isInteger(numericValue) ? `$${numericValue}` : `$${numericValue.toFixed(2)}`;
};

export const normalizeMobilityLinePricingForEditor = (product = {}) => {
    const source = product.linePricing || {};
    const fallbackPrice = numberFromInput(product.price, 0);
    let mode = validModes.has(source.mode) ? source.mode : '';

    if (!mode) {
        if (product.id === 'telus-personal-canada-100gb-ffh') {
            mode = 'totalLineCountTier';
        } else if (product.id === 'telus-epp-rbc-175gb') {
            mode = 'firstLineAdditional';
        } else {
            mode = 'flatPerLine';
        }
    }

    const defaultTiers = product.id === 'telus-personal-canada-100gb-ffh'
        ? [
            { minLines: 1, maxLines: 3, pricePerLine: 45, label: '1-3 lines' },
            { minLines: 4, maxLines: 4, pricePerLine: 41.25, label: '4 lines' }
        ]
        : [{ minLines: 1, maxLines: '', pricePerLine: fallbackPrice, label: 'All lines' }];

    return {
        mode,
        flatPricePerLine: source.flatPricePerLine ?? fallbackPrice,
        firstLinePrice: source.firstLinePrice ?? (product.id === 'telus-epp-rbc-175gb' ? 40 : fallbackPrice),
        additionalLinePrice: source.additionalLinePrice ?? (product.id === 'telus-epp-rbc-175gb' ? 35 : fallbackPrice),
        maxLines: source.maxLines ?? '',
        noteTemplate: source.noteTemplate || '',
        tiers: normalizeArray(source.tiers).length > 0
            ? normalizeArray(source.tiers).map((tier) => ({
                minLines: tier.minLines ?? 1,
                maxLines: tier.maxLines ?? '',
                pricePerLine: tier.pricePerLine ?? fallbackPrice,
                label: tier.label || ''
            }))
            : defaultTiers
    };
};

export const serializeMobilityLinePricing = (linePricing = {}) => {
    const mode = validModes.has(linePricing.mode) ? linePricing.mode : 'flatPerLine';
    const serialized = {
        mode,
        noteTemplate: String(linePricing.noteTemplate || '').trim()
    };

    if (linePricing.maxLines !== '' && linePricing.maxLines !== null && linePricing.maxLines !== undefined) {
        serialized.maxLines = numberFromInput(linePricing.maxLines);
    }

    if (mode === 'flatPerLine') {
        serialized.flatPricePerLine = numberFromInput(linePricing.flatPricePerLine);
    } else if (mode === 'firstLineAdditional') {
        serialized.firstLinePrice = numberFromInput(linePricing.firstLinePrice);
        serialized.additionalLinePrice = numberFromInput(linePricing.additionalLinePrice);
    } else if (mode === 'totalLineCountTier') {
        serialized.tiers = normalizeArray(linePricing.tiers).map((tier) => ({
            minLines: numberFromInput(tier.minLines, 1),
            maxLines: tier.maxLines === '' || tier.maxLines === null || tier.maxLines === undefined
                ? null
                : numberFromInput(tier.maxLines),
            pricePerLine: numberFromInput(tier.pricePerLine),
            label: String(tier.label || '').trim()
        }));
    }

    return serialized;
};

export const calculateMobilityLinePricingTotal = (linePricing = {}, lineCount = 1, fallbackPrice = 0) => {
    const safeLines = Math.max(1, numberFromInput(lineCount, 1));
    const pricing = serializeMobilityLinePricing({
        ...linePricing,
        mode: linePricing.mode || 'flatPerLine',
        flatPricePerLine: linePricing.flatPricePerLine ?? fallbackPrice
    });

    if (pricing.mode === 'firstLineAdditional') {
        return pricing.firstLinePrice + Math.max(0, safeLines - 1) * pricing.additionalLinePrice;
    }

    if (pricing.mode === 'totalLineCountTier') {
        const sortedTiers = normalizeArray(pricing.tiers)
            .filter((tier) => Number.isFinite(Number(tier.minLines)) && Number.isFinite(Number(tier.pricePerLine)))
            .sort((left, right) => left.minLines - right.minLines);
        const matchingTier = sortedTiers.find((tier) => (
            safeLines >= tier.minLines && (tier.maxLines === null || safeLines <= tier.maxLines)
        )) || sortedTiers[sortedTiers.length - 1];
        return safeLines * numberFromInput(matchingTier?.pricePerLine, fallbackPrice);
    }

    return safeLines * numberFromInput(pricing.flatPricePerLine, fallbackPrice);
};

export const formatMobilityLinePricingNote = (linePricing = {}, fallbackPrice = 0) => {
    const pricing = serializeMobilityLinePricing({
        ...linePricing,
        mode: linePricing.mode || 'flatPerLine',
        flatPricePerLine: linePricing.flatPricePerLine ?? fallbackPrice
    });

    if (pricing.noteTemplate) return pricing.noteTemplate;
    if (pricing.mode === 'firstLineAdditional') {
        return `First line ${formatMoney(pricing.firstLinePrice)}; second and rest ${formatMoney(pricing.additionalLinePrice)}.`;
    }
    if (pricing.mode === 'totalLineCountTier') {
        return normalizeArray(pricing.tiers).map((tier) => {
            const range = tier.maxLines === null || tier.maxLines === undefined
                ? `${tier.minLines}+ lines`
                : tier.minLines === tier.maxLines
                    ? `${tier.minLines} line${tier.minLines === 1 ? '' : 's'}`
                    : `${tier.minLines}-${tier.maxLines} lines`;
            return `${formatMoney(tier.pricePerLine)}/line for ${range}`;
        }).join('; ') + '.';
    }
    return `${formatMoney(pricing.flatPricePerLine)}/line.`;
};

export const getMobilityLinePricingErrors = (linePricing = {}) => {
    const errors = [];
    const mode = linePricing.mode;

    if (!validModes.has(mode)) {
        errors.push('Mobility pricing mode is required.');
        return errors;
    }

    const validatePrice = (field, label) => {
        if (!Number.isFinite(Number(linePricing[field])) || Number(linePricing[field]) < 0) {
            errors.push(`${label} must be a non-negative number.`);
        }
    };

    if (linePricing.maxLines !== '' && linePricing.maxLines !== null && linePricing.maxLines !== undefined
        && (!Number.isFinite(Number(linePricing.maxLines)) || Number(linePricing.maxLines) < 1)) {
        errors.push('Max lines must be a positive number when provided.');
    }

    if (mode === 'flatPerLine') validatePrice('flatPricePerLine', 'Flat price per line');
    if (mode === 'firstLineAdditional') {
        validatePrice('firstLinePrice', 'First line price');
        validatePrice('additionalLinePrice', 'Additional line price');
    }

    if (mode === 'totalLineCountTier') {
        const tiers = normalizeArray(linePricing.tiers);
        if (tiers.length === 0) errors.push('At least one tier is required.');

        const ranges = [];
        tiers.forEach((tier, index) => {
            const minLines = Number(tier.minLines);
            const hasOpenMax = tier.maxLines === '' || tier.maxLines === null || tier.maxLines === undefined;
            const maxLines = hasOpenMax ? null : Number(tier.maxLines);
            const pricePerLine = Number(tier.pricePerLine);

            if (!Number.isFinite(minLines) || minLines < 1) errors.push(`Tier ${index + 1} minimum lines must be positive.`);
            if (!hasOpenMax && (!Number.isFinite(maxLines) || maxLines < 1)) errors.push(`Tier ${index + 1} maximum lines must be positive or blank.`);
            if (!hasOpenMax && Number.isFinite(minLines) && Number.isFinite(maxLines) && maxLines < minLines) {
                errors.push(`Tier ${index + 1} maximum lines cannot be lower than minimum lines.`);
            }
            if (!Number.isFinite(pricePerLine) || pricePerLine < 0) errors.push(`Tier ${index + 1} price per line must be non-negative.`);
            if (Number.isFinite(minLines) && (hasOpenMax || Number.isFinite(maxLines))) {
                ranges.push({ minLines, maxLines: hasOpenMax ? Infinity : maxLines, index });
            }
        });

        ranges.sort((left, right) => left.minLines - right.minLines).forEach((range, index, sortedRanges) => {
            const previous = sortedRanges[index - 1];
            if (previous && range.minLines <= previous.maxLines) {
                errors.push(`Tier ${range.index + 1} overlaps another tier.`);
            }
        });
    }

    return errors;
};
